import React, { useState, useEffect } from 'react';
import { repositories, projects, users } from '../services/api';
import { useProject } from '../context/ProjectContext';
import AlertModal from '../components/AlertModal';

/**
 * Admin Panel Component
 * Button styling (Code Review #32):
 * - Page-level action buttons (Add buttons) use .btn-primary (blue) to open modals
 * - Modal form submit buttons use .btn-modal-primary (gray) inside modal dialogs
 * This follows the page/modal button styling consolidation architecture.
 * See App.css for complete button architecture documentation.
 */
const Admin = () => {
  const { refreshProjects } = useProject();
  const [repos, setRepos] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingRepo, setEditingRepo] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    default_branch: 'main'
  });
  const [projectFormData, setProjectFormData] = useState({
    name: '',
    description: '',
    repository_id: '',
    deploy_script: ''
  });
  const [userFormData, setUserFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false
  });
  const [error, setError] = useState('');
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'error', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([loadRepositories(), loadProjects(), loadUsers()]);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRepositories = async () => {
    try {
      const response = await repositories.getAll();
      setRepos(response.data);
    } catch (error) {
      console.error('Failed to load repositories:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projects.getAll();
      setProjectsList(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await users.getAll();
      setUsersList(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingRepo) {
        await repositories.update(editingRepo.id, formData);
      } else {
        await repositories.create(formData);
      }
      setShowModal(false);
      setEditingRepo(null);
      setFormData({ name: '', url: '', default_branch: 'main' });
      loadRepositories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save repository');
    }
  };

  const handleEdit = (repo) => {
    setEditingRepo(repo);
    setFormData({
      name: repo.name,
      url: repo.url,
      default_branch: repo.default_branch
    });
    setShowModal(true);
  };

  const handleDelete = (repo) => {
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to deactivate "${repo.name}"?`,
      onConfirm: async () => {
        try {
          await repositories.delete(repo.id);
          loadRepositories();
        } catch (error) {
          setAlertModal({
            isOpen: true,
            type: 'error',
            message: 'Failed to delete repository'
          });
        }
      }
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    setEditingRepo(null);
    setFormData({ name: '', url: '', default_branch: 'main' });
    setShowModal(true);
  };

  // Project handlers
  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingProject) {
        await projects.update(editingProject.id, projectFormData);
      } else {
        await projects.create(projectFormData);
      }
      setShowProjectModal(false);
      setEditingProject(null);
      setProjectFormData({ name: '', description: '', repository_id: '', deploy_script: '' });
      loadProjects();
      refreshProjects(); // Refresh global project context
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save project');
    }
  };

  const handleProjectEdit = (project) => {
    setEditingProject(project);
    setProjectFormData({
      name: project.name,
      description: project.description || '',
      repository_id: project.repository_id,
      deploy_script: project.deploy_script || ''
    });
    setShowProjectModal(true);
  };

  const handleProjectDelete = (project) => {
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete "${project.name}"?`,
      onConfirm: async () => {
        try {
          await projects.delete(project.id);
          loadProjects();
          refreshProjects(); // Refresh global project context
        } catch (error) {
          setAlertModal({
            isOpen: true,
            type: 'error',
            message: 'Failed to delete project'
          });
        }
      }
    });
  };

  const handleProjectChange = (e) => {
    setProjectFormData({ ...projectFormData, [e.target.name]: e.target.value });
  };

  const openAddProjectModal = () => {
    setEditingProject(null);
    setProjectFormData({ name: '', description: '', repository_id: repos[0]?.id || '', deploy_script: '' });
    setShowProjectModal(true);
  };

  // User handlers
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        // Only send password if it's not empty
        const updateData = { ...userFormData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await users.update(editingUser.id, updateData);
      } else {
        await users.create(userFormData);
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserFormData({ username: '', email: '', password: '', is_admin: false });
      loadUsers();
      setAlertModal({
        isOpen: true,
        type: 'success',
        message: editingUser ? 'User updated successfully' : 'User created successfully. They will need to reset their password on first login.'
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleUserEdit = (user) => {
    setEditingUser(user);
    setUserFormData({
      username: user.username,
      email: user.email,
      password: '',
      is_admin: user.is_admin
    });
    setShowUserModal(true);
  };

  const handleUserDelete = (user) => {
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await users.delete(user.id);
          loadUsers();
          setAlertModal({
            isOpen: true,
            type: 'success',
            message: 'User deleted successfully'
          });
        } catch (error) {
          setAlertModal({
            isOpen: true,
            type: 'error',
            message: error.response?.data?.error || 'Failed to delete user'
          });
        }
      }
    });
  };

  const handleUserChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setUserFormData({ ...userFormData, [e.target.name]: value });
  };

  const openAddUserModal = () => {
    setEditingUser(null);
    setUserFormData({ username: '', email: '', password: '', is_admin: false });
    setShowUserModal(true);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>⚙️ Admin Panel</h1>
        <p>Manage repositories and system settings</p>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2>Repositories</h2>
          <button onClick={openAddModal} className="btn-primary btn-sm">
            + Add Repository
          </button>
        </div>

        {repos.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
            No repositories configured. Add your first repository to get started.
          </p>
        ) : (
          <ul className="repo-list">
            {repos.map((repo) => (
              <li key={repo.id} className="repo-item">
                <div className="repo-info">
                  <h4>{repo.name}</h4>
                  <p>{repo.url}</p>
                  <small style={{ color: '#64748b' }}>
                    Default branch: {repo.default_branch}
                  </small>
                </div>
                <div className="repo-actions">
                  <button
                    onClick={() => handleEdit(repo)}
                    className="btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(repo)}
                    className="btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRepo ? 'Edit Repository' : 'Add New Repository'}</h3>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Repository Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="my-awesome-project"
                  required
                />
              </div>

              <div className="form-group">
                <label>Repository URL *</label>
                <input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://github.com/username/repository"
                  required
                />
                <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Supports HTTPS, SSH, or shorthand (user/repo) format
                </small>
              </div>

              <div className="form-group">
                <label>Default Branch *</label>
                <input
                  type="text"
                  name="default_branch"
                  value={formData.default_branch}
                  onChange={handleChange}
                  placeholder="main"
                  required
                />
                <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Usually 'main' or 'master'
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-modal-primary">
                  {editingRepo ? 'Update' : 'Add'} Repository
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Projects Section */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2>Projects</h2>
          <button onClick={openAddProjectModal} className="btn-primary btn-sm" disabled={repos.length === 0}>
            + Add Project
          </button>
        </div>

        {repos.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
            Please add a repository first before creating projects.
          </p>
        ) : projectsList.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
            No projects configured. Add your first project to get started.
          </p>
        ) : (
          <ul className="repo-list">
            {projectsList.map((project) => (
              <li key={project.id} className="repo-item">
                <div className="repo-info">
                  <h4>{project.name}</h4>
                  <p>{project.description}</p>
                  <small style={{ color: '#64748b' }}>
                    Repository: {project.repository.name}
                  </small>
                </div>
                <div className="repo-actions">
                  <button
                    onClick={() => handleProjectEdit(project)}
                    className="btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleProjectDelete(project)}
                    className="btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="modal-overlay" onClick={() => setShowProjectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProject ? 'Edit Project' : 'Add New Project'}</h3>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleProjectSubmit}>
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  name="name"
                  value={projectFormData.name}
                  onChange={handleProjectChange}
                  placeholder="My Awesome Project"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={projectFormData.description}
                  onChange={handleProjectChange}
                  rows="3"
                  placeholder="Brief description of the project..."
                />
              </div>

              <div className="form-group">
                <label>Repository *</label>
                <select
                  name="repository_id"
                  value={projectFormData.repository_id}
                  onChange={handleProjectChange}
                  required
                >
                  <option value="">Select a repository...</option>
                  {repos.map((repo) => (
                    <option key={repo.id} value={repo.id}>
                      {repo.name}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                  The repository that tickets for this project will work on
                </small>
              </div>

              <div className="form-group">
                <label>Deploy Script Path</label>
                <input
                  type="text"
                  name="deploy_script"
                  value={projectFormData.deploy_script}
                  onChange={handleProjectChange}
                  placeholder="scripts/deploy.sh"
                />
                <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Path to deploy script in the repository. If set, a deploy button will appear in the Kanban board.
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowProjectModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-modal-primary">
                  {editingProject ? 'Update' : 'Add'} Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Section */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2>Users</h2>
          <button onClick={openAddUserModal} className="btn-primary btn-sm">
            + Add User
          </button>
        </div>

        {usersList.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
            No users found.
          </p>
        ) : (
          <ul className="repo-list">
            {usersList.map((user) => (
              <li key={user.id} className="repo-item">
                <div className="repo-info">
                  <h4>
                    {user.username}
                    {user.is_admin && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        background: '#3b82f6',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'normal'
                      }}>
                        Admin
                      </span>
                    )}
                    {user.must_reset_password && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        background: '#f59e0b',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'normal'
                      }}>
                        Must Reset Password
                      </span>
                    )}
                  </h4>
                  <p>{user.email}</p>
                  <small style={{ color: '#64748b' }}>
                    Created: {new Date(user.created_at).toLocaleDateString()}
                  </small>
                </div>
                <div className="repo-actions">
                  <button
                    onClick={() => handleUserEdit(user)}
                    className="btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleUserDelete(user)}
                    className="btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleUserSubmit}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  name="username"
                  value={userFormData.username}
                  onChange={handleUserChange}
                  placeholder="johndoe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={userFormData.email}
                  onChange={handleUserChange}
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                <input
                  type="password"
                  name="password"
                  value={userFormData.password}
                  onChange={handleUserChange}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                  required={!editingUser}
                  minLength="6"
                />
                <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                  {editingUser
                    ? 'User will be required to reset password on next login if changed'
                    : 'User will be required to reset this password on first login'}
                </small>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_admin"
                    checked={userFormData.is_admin}
                    onChange={handleUserChange}
                    style={{ marginRight: '8px', width: 'auto' }}
                  />
                  Administrator
                </label>
                <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Administrators can manage users, repositories, and projects
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-modal-primary">
                  {editingUser ? 'Update' : 'Create'} User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>System Information</h3>
        <div style={{ color: '#64748b', lineHeight: '1.8' }}>
          <p><strong>Backend:</strong> Flask + SQLAlchemy</p>
          <p><strong>Frontend:</strong> React 18</p>
          <p><strong>Authentication:</strong> JWT</p>
          <p><strong>Cloud Provider:</strong> Google Cloud Platform</p>
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        type={alertModal.type}
        message={alertModal.message}
      />

      <AlertModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type="confirm"
        message={confirmModal.message}
        confirmText="Yes, Delete"
        onConfirm={() => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          if (confirmModal.onConfirm) confirmModal.onConfirm();
        }}
      />
    </div>
  );
};

export default Admin;
